import { useEffect, useState } from "react";
import { API } from "../../lib/api";
import { EMPLOYEE_PHOTO_CHANGED } from "../../lib/employeePhoto";

/**
 * Avatar that fetches the employee's photo via /api/employees/{id}/avatar (auth-bearer).
 * Falls back to initials if no photo. Uses blob URL so token stays in header.
 * Bump `refreshKey` prop to force reload.
 *
 * `hasAvatar` (brief bos, laporan performa): GET /employees kini mengirim `has_avatar`
 * per baris (lihat backend/routers/employees.py) supaya daftar karyawan tidak perlu
 * menembak /employees/{id}/avatar untuk SETIAP baris — sebelumnya 60 karyawan tanpa
 * foto = 60 request 404. Default `true` supaya pemanggil lama yang belum mengirim flag
 * ini (mis. avatar user login di sidebar, Leaderboard, MyDashboard) tetap berperilaku
 * seperti sebelumnya: coba fetch, baru fallback ke inisial kalau gagal.
 */
export default function EmployeeAvatar({ employeeId, nama, size = 40, refreshKey = 0, className = "", hasAvatar = true }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [photoVersion, setPhotoVersion] = useState(0);

  useEffect(() => {
    const onPhotoChanged = event => {
      if (event.detail?.employeeId === employeeId) setPhotoVersion(v => v + 1);
    };
    window.addEventListener(EMPLOYEE_PHOTO_CHANGED, onPhotoChanged);
    return () => window.removeEventListener(EMPLOYEE_PHOTO_CHANGED, onPhotoChanged);
  }, [employeeId]);

  useEffect(() => {
    setBlobUrl(null);
    if (!employeeId || (!hasAvatar && !photoVersion)) return;
    let cancelled = false;
    let url;
    const token = localStorage.getItem("hris_token");
    fetch(`${API}/employees/${employeeId}/avatar`, { headers: { Authorization: `Bearer ${token}` }, cache: "no-store" })
      .then(r => r.ok ? r.blob() : null)
      .then(b => {
        if (cancelled || !b) return;
        url = URL.createObjectURL(b);
        setBlobUrl(url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [employeeId, refreshKey, hasAvatar, photoVersion]);

  const initials = (nama || "?").split(" ").slice(0, 2).map(x => x[0] || "").join("").toUpperCase() || "?";
  const s = { width: size, height: size };
  const fontSize = Math.max(10, Math.floor(size * 0.36));

  if (blobUrl) {
    return <img src={blobUrl} alt={nama || "avatar"} style={s} className={`rounded-full object-cover ${className}`} />;
  }
  return (
    <div style={s} className={`rounded-full bg-blue-50 text-blue-600 grid place-items-center font-semibold ${className}`}>
      <span style={{ fontSize }}>{initials}</span>
    </div>
  );
}
